import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import Cs from '../../../services/CommonService';
import AlumniService from './AlumniService';
import FormTemplateService from '../../../services/FormTemplateService';
import { usePageTitle } from '../../../hooks/pageHead';
import { useCurrentUserHook } from '../../Authentication/useUserHook';
import { Spinner } from '../../Common/Spinner';
import { NavIcon } from '../../Common/MenuItem';
import { HelpIcon } from '../../Help/HelpSidePanel';
import Tabs, { TabPane } from 'rc-tabs';
import { ThemeContext } from '../../../contexts/Theme/ThemeContext';

const EmptyState = ({ label }) => (
    <div className="flex aligncenter justifycenter font-13 color-grey p-30">
        {label}
    </div>
);

const SAMPLE_FOCUS_AREAS = ['Startup Mentoring', 'Product Strategy', 'Fundraising', 'Hardware Design'];
const SAMPLE_CERTIFICATIONS = [
    { title: 'PMP Certification', year: '2023' },
    { title: 'AWS Solutions Architect', year: '2022' },
];
const SAMPLE_EXPERIENCE = [
    { title: 'Senior Engineer at TechCorp', status: 'Current' },
    { title: 'Founder at StartupX', status: 'Past' },
];
const SAMPLE_NEWS = [
    { title: 'Promoted to Engineering Lead', date: 'Jul 10, 2026' },
    { title: 'Joined the Alumni Mentorship Program', date: 'Jun 2, 2026' },
];

const AlumniProfilePage = () => {
    usePageTitle('Alumni Profile', 'alumni');

    const { id } = useParams();
    const { currentUser } = useCurrentUserHook();
    const { theme, toggleTheme } = useContext(ThemeContext);

    const [alumni, setAlumni] = useState();
    const [formFields, setFormFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSubTab, setActiveSubTab] = useState('updates');
    const [profileOpen, setProfileOpen] = useState(false);

    useEffect(() => {
        getAlumni();
    }, [id]);

    const getAlumni = async () => {
        setLoading(true);
        const { status, data } = await AlumniService.get(id);
        if (status === 200) {
            setAlumni(data);
            getFormFields(data.form_id || 1263);
        } else {
            setLoading(false);
        }
    };

    const getFormFields = (formId) => {
        FormTemplateService.getFormFields({ ids: [formId] }).then((res) => {
            if (res.status === 200) {
                setFormFields(res.data.form_fields[formId]);
            }
            setLoading(false);
        });
    };

    const getInitials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

    const getValue = (field) => {
        const raw = alumni?.data_source?.[field.client_id] ?? alumni?.data?.[field.client_id];
        if (raw == null || raw === '') return '-';
        if (Array.isArray(raw)) return raw.map(v => v?.label ?? v).join(', ') || '-';
        if (typeof raw === 'object') return raw.label ?? '-';
        return String(raw);
    };

    const getValueByLabel = (label) => {
        const field = formFields.find(f => f.label === label);
        return field ? getValue(field) : null;
    };

    if (loading) return <Spinner />;

    const data = alumni?.data || {};
    const industry = getValueByLabel('Primary Industry');
    const specialization = getValueByLabel('Specialization');

    return (
        <div id="ct" data-theme={theme}>
            <div className="app-content">
                <div className="app-content-section">
                    <div className="cover-gradient brd-10">
                        <div className="py-4 px-3 text-white pos-rel">
                            <span className="pos-abs t-0 r-15 m-t-10 m-r-10 pointer" onClick={() => toggleTheme()}
                                data-tip={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                                <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'} white font-16`} />
                            </span>

                            <div className="flex aligncenter gap-20 m-t-10">
                                <div className="circle-avatar-lg bg-highlight-2 flex aligncenter justifycenter fw700 f22">
                                    {getInitials(data.name)}
                                </div>
                                <div>
                                    <h2 className="f18 bold-600">{data.name || 'Alumni Profile'}</h2>
                                    {(industry || specialization) &&
                                        <div className="font-14 m-t-2">
                                            {industry && industry !== '-' ? industry : null}
                                            {industry && industry !== '-' && specialization && specialization !== '-' ? ' · ' : null}
                                            {specialization && specialization !== '-' ? specialization : null}
                                        </div>
                                    }
                                    <div className="font-13 m-t-5">
                                        #{alumni?.record_id} · Updated {Cs.formatUpdateDate(alumni?.updated_at)}
                                    </div>
                                </div>
                            </div>

                            <div className="m-t-15 pointer" onClick={() => setProfileOpen(!profileOpen)}>
                                <span className="font-13 fw500 text-white underline">
                                    {profileOpen ? 'Show less details' : 'Show more details'}
                                </span>
                                <span className={`m-l-5 ${profileOpen ? 'fas fa-angle-up f16' : 'fas fa-angle-down f16'}`} />
                            </div>
                        </div>

                        {profileOpen && (
                            <div className="bg-white p-15 brd-b-10">
                                <div className="row gy-1">
                                    {formFields.map((f, k) => (
                                        <div className="col-md-4" key={k}>
                                            <div className="form-label">{f.label}</div>
                                            <div className="fw500 m-t-2">{getValue(f)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="clearfix bg-white m-t-15 bs-1 p-15 brd-10">
                        <div className="f20 fw500 f18-xs m-b-10">
                            PROFESSIONAL DETAILS
                        </div>
                        <Tabs defaultActiveKey="focus" className="bg-white" moreIcon="+">
                            <TabPane tab="Focus Areas" key="focus">
                                <div className="flex wrap gap-10 p-t-15">
                                    {SAMPLE_FOCUS_AREAS.map((area, i) => (
                                        <span key={i} className="badge brd-5 bg-lblue-1 text-blue-2 f13 fw500">{area}</span>
                                    ))}
                                </div>
                            </TabPane>
                            <TabPane tab="Certifications" key="certifications">
                                <div className="p-t-10">
                                    {SAMPLE_CERTIFICATIONS.map((c, i) => (
                                        <div key={i} className="flex justspacebetween aligncenter br-btm-grey p-t-10 p-b-10">
                                            <span className="fw500 f14">{c.title}</span>
                                            <span className="font-13 color-grey">{c.year}</span>
                                        </div>
                                    ))}
                                </div>
                            </TabPane>
                            <TabPane tab="Experience" key="experience">
                                <div className="p-t-10">
                                    {SAMPLE_EXPERIENCE.map((e, i) => (
                                        <div key={i} className="flex justspacebetween aligncenter br-btm-grey p-t-10 p-b-10">
                                            <span className="fw500 f14">{e.title}</span>
                                            <span className="badge-2">{e.status}</span>
                                        </div>
                                    ))}
                                </div>
                            </TabPane>
                        </Tabs>
                    </div>

                    <div className="clearfix bg-white m-t-15 bs-1 p-15 brd-10">
                        <div className="f20 fw500 f18-xs m-b-10">
                            NEWS
                        </div>
                        {SAMPLE_NEWS.map((n, i) => (
                            <div key={i} className="flex justspacebetween aligncenter br-btm-grey p-t-10 p-b-10">
                                <span className="fw500 f14">{n.title}</span>
                                <span className="font-13 color-grey">{n.date}</span>
                            </div>
                        ))}
                    </div>

                    <div className="clearfix bg-white m-t-15 bs-1 p-15 brd-10">
                        <Tabs activeKey={activeSubTab} onChange={setActiveSubTab} className="bg-white" moreIcon="+">
                            <TabPane tab="Updates" key="updates">
                                <EmptyState label="Updates for this alumnus will appear here" />
                            </TabPane>
                            <TabPane tab="Mentorship" key="mentorship">
                                <div className="flex justspacebetween aligncenter m-b-10">
                                    <NavIcon id="mentorship_filter" dataTip="Filter" className="bg-highlight round-btn" icon="fas fa-filter" onSelect={() => { }} />
                                    <NavIcon id="mentorship_add" dataTip="Add Request" className="bg-highlight round-btn m-l-10" icon="fas fa-plus" onSelect={() => { }} />
                                </div>
                                <EmptyState label="Mentorship requests coming soon" />
                            </TabPane>
                        </Tabs>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AlumniProfilePage;